import React, { useState, useEffect, useRef } from "react";
import { motion, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ChevronRight, ChevronUp, ChevronDown } from "lucide-react";
import "./service.css";

// Stacked Card Component
const StackedServiceCard = ({ title, description, ctaText, ctaLink, index, progress, totalCards }) => {
  // Calculate transforms based on scroll progress
  const isActive = progress >= index && progress < index + 1;
  const hasScrolledPast = progress >= index + 1;
  const isUpcoming = progress < index;
  
  // Peel effect for the active card being scrolled away
  const peelAmount = isActive ? (progress - index) : 0;
  const peelProgress = useMotionValue(peelAmount);
  
  // Update motion value when peelAmount changes
  useEffect(() => {
    peelProgress.set(peelAmount);
  }, [peelAmount, peelProgress]);
  
  const rotateX = useTransform(
    useSpring(peelProgress, { stiffness: 100, damping: 20 }),
    [0, 1],
    [0, -12]
  );
  const translateY = useTransform(
    useSpring(peelProgress, { stiffness: 100, damping: 20 }),
    [0, 1],
    [0, -120]
  );
  const opacity = useTransform(
    useSpring(peelProgress, { stiffness: 100, damping: 20 }),
    [0, 0.5, 1],
    [1, 0.6, 0]
  );
  
  const shimmerX = useTransform(peelProgress, [0, 1], ["-100%", "100%"]);

  // Calculate position based on whether card has been scrolled past or is upcoming
  const yPosition = hasScrolledPast ? -200 : (isUpcoming ? 40 : 0);
  const scale = hasScrolledPast ? 0.9 : (isUpcoming ? 0.95 : 1);
  const cardOpacity = hasScrolledPast ? 0 : 1;

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center px-3 sm:px-4 md:px-6"
      style={{
        zIndex: totalCards - index,
        pointerEvents: isActive ? 'auto' : 'none',
      }}
      initial={{ opacity: 0 }}
      animate={{ 
        opacity: cardOpacity,
        y: yPosition,
        scale: scale,
      }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <motion.div
        className="glass-card w-full max-w-6xl h-[75vh] sm:h-[80vh] p-6 sm:p-8 md:p-12 relative overflow-hidden group cursor-pointer flex flex-col justify-between bg-gradient-to-br from-gray-900 to-black border-gray-700/50"
        style={{
          rotateX: isActive ? rotateX : 0,
          y: isActive ? translateY : 0,
          opacity: isActive ? opacity : 1,
          transformStyle: "preserve-3d",
          perspective: 1200,
        }}
      >
        {/* Card number indicator */}
        <div className="absolute top-4 sm:top-6 md:top-8 right-4 sm:right-6 md:right-8 text-white/5 font-bold text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-orbitron pointer-events-none">
          {String(index + 1).padStart(2, '0')}
        </div>

        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 sm:h-1.5 bg-gray-700/50">
          <motion.div 
            className="h-full bg-gradient-to-r from-gray-500 to-white"
            style={{
              scaleX: isActive ? peelProgress : (hasScrolledPast ? 1 : 0),
              transformOrigin: "left"
            }}
          />
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center max-w-4xl">
          <motion.h3 
            className="card-title text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 font-orbitron leading-tight"
          >
            {title}
          </motion.h3>
          
          <motion.p 
            className="card-description text-gray-200 text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 leading-relaxed max-w-3xl"
          >
            {description}
          </motion.p>

          <motion.a
            href={ctaLink}
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 text-white font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-white/10 text-base sm:text-lg self-start border border-gray-600"
            whileHover={{ scale: 1.05, x: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            {ctaText}
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </motion.a>
        </div>

        {/* Shimmer effect on scroll */}
        {isActive && (
          <motion.div
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none"
            style={{ x: shimmerX }}
          />
        )}
      </motion.div>
    </motion.div>
  );
};



const Services = () => {
  const cardsContainerRef = useRef(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isInCardSection, setIsInCardSection] = useState(false);

  const services = [
    { 
      title: "Algo Trading Academy",
      description: "Join Africa's premier algorithmic trading education program. Our comprehensive training pipeline takes you from beginner to professional quant: Learn Python/MQL5 coding → Build profitable algorithmic systems → Unlock pathways to proprietary trading capital.",
      ctaText: "Join Academy",
      ctaLink: "/algo-trading-academy"
    },
    { 
      title: "Algorithm Development", 
      description: "We specialize in creating custom algorithmic trading solutions that automate decision-making in the financial markets. Our algorithms are designed to identify market opportunities and execute trades faster than human capability, ensuring accuracy and maximizing profitability.",
      ctaText: "Book Consultation",
      ctaLink: "/contact-us"
    },
    { 
      title: "Portfolio Management", 
      description: "Our portfolio management services leverage cutting-edge quantitative models to optimize performance. We use data analysis and automation to execute trades with precision and agility, ensuring consistent returns while minimizing risk.",
      ctaText: "Learn More",
      ctaLink: "/investment-funds"
    },
    { 
      title: "Quantitative Research & Development", 
      description: "Our team of quantitative researchers is dedicated to developing robust financial models and strategies. We provide in-depth research into market dynamics, backtesting, and optimization to help you make informed investment decisions.",
      ctaText: "Contact Us",
      ctaLink: "/contact-us"
    },
    { 
      title: "Consulting Services", 
      description: "KSQuantitative provides expert consulting services to help you integrate advanced quantitative strategies into your investment approach. Our consultancy covers everything from portfolio optimization to strategy refinement.",
      ctaText: "Book Consultation (Google Meet)",
      ctaLink: "https://calendar.app.google/uRXac587SAi2JAeKA"
    },
    { 
      title: "Ready to elevate your trading?", 
      description: "Explore our innovative solutions today, and let KSQuantitative be your partner in success. Join hundreds of successful traders who have transformed their trading with our cutting-edge solutions.",
      ctaText: "Get Started",
      ctaLink: "/contact-us"
    }
  ];

  const isScrolling = useRef(false);

  // Wheel event handler for scroll hijacking
  useEffect(() => {
    const handleWheel = (e) => {
      if (!isInCardSection) return;
      
      e.preventDefault();
      
      if (isScrolling.current) return;
      
      const delta = e.deltaY;
      const threshold = 30;
      
      if (Math.abs(delta) > threshold) {
        if (delta > 0 && currentCardIndex < services.length - 1) {
          // Scroll down
          isScrolling.current = true;
          setCurrentCardIndex(prev => prev + 1);
          setTimeout(() => isScrolling.current = false, 800);
        } else if (delta < 0 && currentCardIndex > 0) {
          // Scroll up
          isScrolling.current = true;
          setCurrentCardIndex(prev => prev - 1);
          setTimeout(() => isScrolling.current = false, 800);
        } else if (delta > 0 && currentCardIndex === services.length - 1) {
          // Release scroll at the end
          setIsInCardSection(false);
        }
      }
    };

    const cardsContainer = cardsContainerRef.current;
    if (cardsContainer && isInCardSection) {
      cardsContainer.addEventListener('wheel', handleWheel, { passive: false });
      return () => cardsContainer.removeEventListener('wheel', handleWheel);
    }
  }, [isInCardSection, currentCardIndex, services.length]);

  // Intersection observer to detect when cards section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInCardSection(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (cardsContainerRef.current) {
      observer.observe(cardsContainerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Progress value for animations
  const progress = currentCardIndex;

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen w-full flex flex-col justify-center items-center overflow-hidden">
        {/* Background particles - subtle only */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-blue-400/20 rounded-full"
              animate={{
                x: [Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000), Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000)],
                y: [Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800), Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800)],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ))}
        </div>

        <div className="relative z-10 w-full px-4 text-center">
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white font-bold mb-4 font-orbitron"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Our Services
            </span>
          </motion.h1>
          
          <motion.p
            className="text-gray-300 text-lg sm:text-xl max-w-2xl mx-auto mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Scroll down to explore our premium offerings
          </motion.p>

          <motion.div
            className="flex flex-col items-center gap-2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <div className="text-blue-400 text-sm">Scroll to discover</div>
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </motion.div>
        </div>
      </section>

      {/* Stacked Cards Section with Scroll Snapping */}
      <section 
        ref={cardsContainerRef}
        className="relative h-screen w-full overflow-hidden"
      >
        <div className="sticky top-0 h-screen w-full">
          {/* Container for all stacked cards */}
          <div className="relative h-full w-full">
            {services.map((service, index) => (
              <StackedServiceCard
                key={index}
                {...service}
                index={index}
                progress={progress}
                totalCards={services.length}
              />
            ))}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="fixed right-4 sm:right-6 md:right-8 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2 sm:gap-3">
          {services.map((_, index) => (
            <motion.div
              key={index}
              className={`w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full transition-all duration-300 cursor-pointer`}
              animate={{
                height: currentCardIndex === index ? 24 : 6,
                width: currentCardIndex === index ? 6 : 6,
                  backgroundColor: currentCardIndex === index
                  ? '#ffffff'
                  : currentCardIndex > index
                  ? 'rgba(255, 255, 255, 0.5)'
                  : '#4b5563'
              }}
              onClick={() => setCurrentCardIndex(index)}
            />
          ))}
        </div>

        {/* Mobile Navigation Controls */}
        <div className="fixed bottom-8 left-0 right-0 z-50 flex justify-center gap-4 sm:hidden pointer-events-none">
          <motion.button
            className={`p-3 rounded-full bg-gray-800/80 text-white backdrop-blur-sm border border-gray-600 shadow-lg pointer-events-auto ${currentCardIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (currentCardIndex > 0) setCurrentCardIndex(prev => prev - 1);
            }}
            whileTap={{ scale: 0.95 }}
            disabled={currentCardIndex === 0}
          >
            <ChevronUp className="w-6 h-6" />
          </motion.button>
          
          <motion.button
            className={`p-3 rounded-full bg-blue-600/80 text-white backdrop-blur-sm border border-blue-400 shadow-lg pointer-events-auto ${currentCardIndex === services.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-500'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (currentCardIndex < services.length - 1) setCurrentCardIndex(prev => prev + 1);
            }}
            whileTap={{ scale: 0.95 }}
            disabled={currentCardIndex === services.length - 1}
          >
            <ChevronDown className="w-6 h-6" />
          </motion.button>
        </div>
      </section>

      {/* Footer Spacer */}
      <div className="h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center text-white px-4"
        >
          <h2 className="text-4xl font-bold mb-4 font-orbitron">Ready to Get Started?</h2>
          <p className="text-xl text-gray-300 mb-8">Contact us today to learn more about our services</p>
          <motion.a
            href="/contact-us"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-white/10 text-lg border border-gray-600"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Get in Touch
            <ChevronRight className="w-6 h-6" />
          </motion.a>
        </motion.div>
      </div>
    </>
  );
};

export default Services;
